import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { clearHistory, setDrawerAlert, setStage } from 'slices/drawer'
import { useFirebaseEvent, useForm } from 'hooks'
import { formatPhone, formatPhoneToDB } from 'utils'
import {
  REGISTRATION_DATA_STAGES,
  DEFAULT_ERROR_MESSAGE,
  FIREBASE_EVENTS,
  EVENT_STATUS,
  CONTACT_OPTIONS,
} from 'app-constants'
import { tokenService } from 'services'
import { Card } from 'components/card'
import { Input } from 'components/input'
import { Button } from 'components/button'
import { CountdownTime } from 'components/countdown-time'
import { SEND_CODE_INITIAL_STATE } from '../../constants'
import './style.scss'

const validationSchema = Yup.object().shape({
  code: Yup.string().required(),
})

export function PhoneConfirmation({ data }) {
  const [sendCodeInfo, setSendCodeInfo] = useState(SEND_CODE_INITIAL_STATE)
  const { document } = useSelector(({ customer }) => customer.info)
  const { sendEvent } = useFirebaseEvent()
  const dispatch = useDispatch()
  const { generateToken, confirmToken } = tokenService()

  const form = useForm({
    initialValues: {
      code: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    sendCode()
  }, [data.contact, dispatch])

  async function sendCode() {
    try {
      const formattedPhone = formatPhoneToDB(data.contact)

      await generateToken(document, formattedPhone, CONTACT_OPTIONS.SMS)
    } catch (err) {
      const errorMessage = err?.message || DEFAULT_ERROR_MESSAGE
      console.error(err)
      dispatch(setDrawerAlert({ severity: 'error', title: errorMessage }))
    }
  }

  async function handleSendCode() {
    try {
      setSendCodeInfo(prev => ({ ...prev, isLoading: true }))
      await sendCode()
      setSendCodeInfo(prev => ({
        ...prev,
        message: 'Enviamos um novo código para você.',
        hasError: false,
        canSendCode: false,
      }))
    } catch (err) {
      const errorMessage = err?.message || DEFAULT_ERROR_MESSAGE
      console.error(err)
      dispatch(setDrawerAlert({ severity: 'error', title: errorMessage }))
    } finally {
      setSendCodeInfo(prev => ({ ...prev, isLoading: false }))
    }
  }

  async function handleSubmit(values) {
    try {
      sendEvent(FIREBASE_EVENTS.CLICK_ON_ADD_PHONE_CONFIRM_TOKEN)

      await confirmToken(document, values.code)

      sendEvent(FIREBASE_EVENTS.ADD_PHONE_SUCCESS, EVENT_STATUS.SUCCESS)
      dispatch(setStage(REGISTRATION_DATA_STAGES.CONTACT_CONFIRMATION))
      dispatch(clearHistory())
    } catch (error) {
      const { message } = error?.response?.data || DEFAULT_ERROR_MESSAGE

      sendEvent(FIREBASE_EVENTS.ADD_PHONE_ERROR, EVENT_STATUS.ERROR, error?.response?.status)
      dispatch(setDrawerAlert({ severity: 'error', title: message }))
    }
  }

  function renderPhone() {
    return formatPhone(data.contact)
  }

  function renderSendCodeButton() {
    const { canSendCode } = sendCodeInfo
    if (canSendCode) return null

    return (
      <span className='text'>
        Restam&nbsp;
        <CountdownTime
          canSendCode={canSendCode}
          onFinishCounter={() => {
            setSendCodeInfo({ ...sendCodeInfo, canSendCode: true, hasError: false, message: '' })
          }}
        />
        &nbsp;segundos
      </span>
    )
  }

  function renderSendCodeErrorMessage() {
    const { message, hasError } = sendCodeInfo

    if (!hasError) return null

    return <span className='message'>{message}</span>
  }

  return (
    <div className='phone-confirmation-component'>
      <h3 className='title'>Enviamos um código para o seu telefone atual</h3>
      <span className='subtitle'>
        Enviamos um código para o seu atual telefone. Confirme-o abaixo para completar a alteração.
      </span>
      <Card className='card'>
        <span className='title'>Telefone</span>
        <span className='text'>{renderPhone()}</span>
      </Card>
      <form className='form' onSubmit={form.handleSubmit}>
        <div className='content'>
          <label className='label'>Inserir código recebido</label>
          <Input
            className='input'
            name='code'
            placeholder='Código recebido por telefone'
            onChange={form.handleChange}
            value={form.values.code}
            hasError={form.errors.code}
          />
          <div className='timer'>
            <Button
              type='button'
              className='button'
              onClick={handleSendCode}
              disabled={!sendCodeInfo.canSendCode}
              color='primary'
              isLoading={sendCodeInfo.isLoading}
            >
              NÃO RECEBI O CÓDIGO
            </Button>
            {renderSendCodeButton()}
          </div>
          {renderSendCodeErrorMessage()}
        </div>
        <Button
          className='button'
          type='submit'
          color='primary'
          size='large'
          isLoading={form.isSubmitting}
        >
          Enviar Código
        </Button>
      </form>
    </div>
  )
}
